import { useEffect, useRef, useState } from 'react'
import { useAlertStore, selectFilteredAlerts } from '../../store/alertStore'
import { useAlertList } from '../../hooks/useAlerts'
import AlertCard from './AlertCard'

type WsStatus = 'connecting' | 'live' | 'reconnecting' | 'reconnected'

const PULL_THRESHOLD = 70

export default function AlertList() {
  const filters = useAlertStore((s) => s.filters)
  const liveConnected = useAlertStore((s) => s.liveConnected)
  const storeAlerts = useAlertStore(selectFilteredAlerts)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const refreshingRef = useRef(false)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch } =
    useAlertList(filters)

  const [pullProgress, setPullProgress] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // Track WebSocket status transitions with reconnecting/reconnected states
  const [wsStatus, setWsStatus] = useState<WsStatus>('connecting')
  const prevConnected = useRef<boolean | null>(null)
  useEffect(() => {
    if (liveConnected) {
      if (prevConnected.current === false) {
        // Was disconnected — flash "Reconnected" before settling to "Live"
        setWsStatus('reconnected')
        const t = setTimeout(() => setWsStatus('live'), 2500)
        prevConnected.current = true
        return () => clearTimeout(t)
      }
      setWsStatus('live')
      prevConnected.current = true
    } else {
      setWsStatus(prevConnected.current === null ? 'connecting' : 'reconnecting')
      prevConnected.current = false
    }
  }, [liveConnected])

  // Seed store from REST response; live updates via useLiveAlerts take priority
  useEffect(() => {
    if (!data) return
    const fetched = data.pages.flatMap((p) => p.items)
    const { upsertAlert } = useAlertStore.getState()
    fetched.forEach((a) => upsertAlert(a))
  }, [data])

  // Infinite scroll sentinel
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage()
      },
      { rootMargin: '100px' }
    )
    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Pull-to-refresh: touchmove must be non-passive to allow preventDefault
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let startY: number | null = null

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop === 0) { startY = e.touches[0].clientY; setIsDragging(true) }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (startY === null) return
      const delta = e.touches[0].clientY - startY
      if (delta > 0) {
        e.preventDefault()
        setPullProgress(Math.min(delta / PULL_THRESHOLD, 1))
      } else {
        startY = null; setIsDragging(false); setPullProgress(0)
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (startY === null) return
      const delta = (e.changedTouches[0]?.clientY ?? 0) - startY
      const shouldRefresh = delta >= PULL_THRESHOLD
      startY = null; setIsDragging(false); setPullProgress(0)
      if (shouldRefresh && !refreshingRef.current) {
        refreshingRef.current = true
        setRefreshing(true)
        refetch().finally(() => { refreshingRef.current = false; setRefreshing(false) })
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [refetch])

  return (
    <div className="flex flex-col h-full w-full min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800 shrink-0">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {storeAlerts.length} alert{storeAlerts.length !== 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${
              wsStatus === 'live'        ? 'bg-green-500' :
              wsStatus === 'reconnected' ? 'bg-green-500' :
              wsStatus === 'reconnecting'? 'bg-amber-500 animate-pulse' :
                                           'bg-gray-400 dark:bg-gray-600 animate-pulse'
            }`} />
            <span className={
              wsStatus === 'live'         ? 'text-green-400' :
              wsStatus === 'reconnected'  ? 'text-green-400' :
              wsStatus === 'reconnecting' ? 'text-amber-400' :
                                            'text-gray-500'
            }>
              {wsStatus === 'live'         ? 'Live' :
               wsStatus === 'reconnected'  ? 'Reconnected ✓' :
               wsStatus === 'reconnecting' ? 'Reconnecting…' :
                                             'Connecting…'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => { if (!refreshingRef.current) { refreshingRef.current = true; setRefreshing(true); refetch().finally(() => { refreshingRef.current = false; setRefreshing(false) }) } }}
            disabled={refreshing}
            title="Refresh alerts"
            className="text-gray-500 hover:text-gray-700 disabled:text-gray-300 dark:text-gray-600 dark:hover:text-gray-300 dark:disabled:text-gray-700 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Pull-to-refresh indicator */}
      <div
        className={`flex items-center justify-center shrink-0 overflow-hidden ${isDragging ? '' : 'transition-all duration-200'}`}
        style={{ height: refreshing ? 40 : pullProgress * 40 }}
      >
        {refreshing ? (
          <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin" />
        ) : (
          <span className="text-gray-500 text-xs">
            {pullProgress >= 1 ? '↑ Release to refresh' : '↓ Pull to refresh'}
          </span>
        )}
      </div>

      {/* List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain p-2 space-y-1.5">
        {isLoading && (
          <div className="text-center text-gray-500 text-sm py-8">Loading alerts…</div>
        )}
        {isError && (
          <div className="text-center text-red-400 text-sm py-8">Failed to load alerts</div>
        )}
        {!isLoading && storeAlerts.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-600 text-sm py-8">No alerts match current filters</div>
        )}
        {storeAlerts.map((alert) => (
          <AlertCard key={alert.id} alert={alert} />
        ))}
        <div ref={loadMoreRef} className="py-1">
          {isFetchingNextPage && (
            <div className="text-center text-gray-500 text-xs">Loading more…</div>
          )}
        </div>
      </div>
    </div>
  )
}
